import { useEffect, useState } from "react";
import { RatingResponseDto, Profile } from "../../../types/types";
import { StarIcon } from "@heroicons/react/24/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Carousel from "./carousel";

type RatingsProps = {
  member: Profile;
  ratings: RatingResponseDto[];
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  isLoading?: boolean;
  onPageChange?: (page: number) => void;
};

const Ratings = ({
  member,
  ratings: incomingRatings,
  currentPage = 0,
  totalPages = 1,
  totalElements,
  isLoading = false,
  onPageChange,
}: RatingsProps) => {
  const { t } = useTranslation("publicProfile");
  const navigate = useNavigate();
  const location = useLocation();
  const [ratings, setRatings] = useState<RatingResponseDto[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [animatedValues, setAnimatedValues] = useState<Record<number, number>>(
    {},
  );

  useEffect(() => {
    setRatings(incomingRatings);
    setAnimatedValues({});
    incomingRatings.forEach((item, index) => {
      setTimeout(() => animateRating(item.id, item.rating), 150 + index * 100);
    });
  }, [incomingRatings]);

  const animateRating = (id: number, finalValue: number) => {
    let current = 0;
    const duration = 800;
    const steps = 40;
    const increment = finalValue / steps;
    const intervalDelay = duration / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= finalValue) {
        current = finalValue;
        clearInterval(timer);
      }

      setAnimatedValues((prev) => ({
        ...prev,
        [id]: Number(current.toFixed(1)),
      }));
    }, intervalDelay);
  };

  const truncateName = (
    firstName: string,
    lastName: string,
    maxLength = 24,
  ) => {
    const full = `${firstName} ${lastName}`.trim();
    if (full.length <= maxLength) return full;

    const truncated = full.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");

    return (
      (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + "…"
    );
  };

  const truncateFeedback = (text: string, maxLength = 5000) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "…";
  };

  const getInitials = (firstName: string, lastName: string) => {
    const f = firstName?.trim()?.[0] ?? "";
    const l = lastName?.trim()?.[0] ?? "";
    return (f + l).toUpperCase();
  };

  const getAvatarClass = (_name: string) => "bg-gray-200 text-gray-600";

  const reviewCount = totalElements ?? ratings.length;
  const canPaginate = Boolean(onPageChange) && totalPages > 1;
  const paginationStart = Math.max(
    0,
    Math.min(currentPage - 1, totalPages - 3),
  );

  const visiblePages = Array.from(
    { length: Math.min(3, totalPages) },
    (_, index) => paginationStart + index,
  );

  const handlePageChange = (page: number) => {
    if (
      !onPageChange ||
      isLoading ||
      page === currentPage ||
      page < 0 ||
      page >= totalPages
    ) {
      return;
    }

    onPageChange(page);
  };

  const navigateToProfile = (profileId?: string | number | null) => {
    if (!profileId) return;
    const profilePath =
      location.pathname === "/view-profile" ? "/view-profile" : "/profile";
    navigate(`${profilePath}?profileId=${profileId}`);
  };

  if (ratings.length === 0) {
    return (
      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900">
            {t("ratings_reviews")}
          </h3>
        </div>

        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center mb-2">
            <StarIcon className="w-5 h-5 text-gray-300" />
          </div>

          <p className="text-gray-400 text-xs">{t("ratings_tab_info")}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">
          {t("ratings_reviews")}
        </h3>

        <span className="text-xs text-gray-400 font-normal">
          {reviewCount}{" "}
          {reviewCount === 1
            ? t("ratings").slice(0, -1) || "review"
            : t("ratings") || "reviews"}
        </span>
      </div>

      <Carousel
        itemClassName="w-[88%] md:w-[46%] lg:w-[23%]"
        gapClassName="gap-3"
        ariaLabelPrev="Previous reviews"
        ariaLabelNext="Next reviews"
      >
        {ratings.map((rating, index) => {
          const animVal = animatedValues[rating.id] ?? 0;
          const fullName = `${rating.profile.firstName ?? ""} ${
            rating.profile.lastName ?? ""
          }`.trim();

          const displayName = truncateName(
            rating.profile.firstName ?? "",
            rating.profile.lastName ?? "",
          );

          const initials = getInitials(
            rating.profile.firstName ?? "",
            rating.profile.lastName ?? "",
          );

          const avatarClass = getAvatarClass(fullName);
          const feedback = truncateFeedback(rating.feedback ?? "");
          const isLong = feedback.length > 110;
          const isExpanded = Boolean(expanded[rating.id]);

          return (
            <div
              key={rating.id}
              className="h-full flex flex-col bg-white border border-gray-100 rounded-2xl p-3"
              style={{
                animation: `fadeInUp 0.35s ease-out ${index * 0.06}s both`,
              }}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {rating.profile.profileImage ? (
                    <img
                      src={rating.profile.profileImage}
                      alt={displayName}
                      className="w-10 h-10 rounded-xl object-cover shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center font-medium text-xs shrink-0 ${avatarClass}`}
                    >
                      {initials}
                    </div>
                  )}

                  <p
                    className="text-gray-900 text-xs truncate"
                    title={fullName}
                  >
                    {displayName}
                  </p>
                </div>

                <div className="flex items-center gap-0.5 shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`w-3 h-3 transition-colors duration-300 ${
                        i < Math.floor(animVal)
                          ? "text-amber-400"
                          : "text-gray-200"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {feedback && (
                <p
                  className={`text-gray-800 text-xs leading-5 text-left break-words ${
                    isExpanded ? "" : "line-clamp-3"
                  }`}
                >
                  {feedback}
                </p>
              )}

              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="text-[11px] text-gray-400 font-normal">
                  {rating.createdAt
                    ? new Date(rating.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })
                    : ""}
                </span>
                {isLong && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded((prev) => ({
                        ...prev,
                        [rating.id]: !prev[rating.id],
                      }));
                    }}
                    className="text-[11px] text-blue-600 hover:underline"
                  >
                    {isExpanded ? "less" : "...more"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </Carousel>

      {canPaginate && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500 font-normal">
            {t("page")} {currentPage + 1} {t("of")} {totalPages}
          </p>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || isLoading}
              className="h-8 w-8 inline-flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous ratings page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {visiblePages.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                className={`h-8 min-w-8 px-2 rounded-xl text-xs font-medium border ${
                  page === currentPage
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                } disabled:cursor-not-allowed transition-colors`}
              >
                {page + 1}
              </button>
            ))}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage + 1 >= totalPages || isLoading}
              className="h-8 w-8 inline-flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next ratings page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default Ratings;
 